"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.ReadableEthersLiquity = void 0;
const lib_base_1 = require("@liquity/lib-base");
const _utils_1 = require("./_utils");
const contracts_1 = require("./contracts");
const EthersLiquityConnection_1 = require("./EthersLiquityConnection");
const BlockPolledLiquityStore_1 = require("./BlockPolledLiquityStore");
const bignumber_1 = require("@ethersproject/bignumber");
// TODO: these are constant in the contracts, so it doesn't make sense to make a call for them,
// but to avoid having to update them here when we change them in the contracts, we could read
// them once after deployment and save them to LiquityDeployment.
const MINUTE_DECAY_FACTOR = lib_base_1.Decimal.from("0.999037758833783000");
const BETA = lib_base_1.Decimal.from(2);
var BackendTroveStatus;
(function (BackendTroveStatus) {
    BackendTroveStatus[BackendTroveStatus["nonExistent"] = 0] = "nonExistent";
    BackendTroveStatus[BackendTroveStatus["active"] = 1] = "active";
    BackendTroveStatus[BackendTroveStatus["closedByOwner"] = 2] = "closedByOwner";
    BackendTroveStatus[BackendTroveStatus["closedByLiquidation"] = 3] = "closedByLiquidation";
    BackendTroveStatus[BackendTroveStatus["closedByRedemption"] = 4] = "closedByRedemption";
})(BackendTroveStatus || (BackendTroveStatus = {}));
const userTroveStatusFrom = (backendStatus) => backendStatus === 0
    ? "nonExistent"
    : backendStatus === 1
        ? "open"
        : backendStatus === 2
            ? "closedByOwner"
            : backendStatus === 3
                ? "closedByLiquidation"
                : backendStatus === 4
                    ? "closedByRedemption"
                    : _utils_1.panic(new Error(`invalid backendStatus ${backendStatus}`));
const convertToDate = (timestamp) => new Date(timestamp * 1000);
const validSortingOptions = ["ascendingCollateralRatio", "descendingCollateralRatio"];
const expectPositiveInt = (obj, key) => {
    if (obj[key] !== undefined) {
        if (!Number.isInteger(obj[key])) {
            throw new Error(`${key} must be an integer`);
        }
        if (obj[key] < 0) {
            throw new Error(`${key} must not be negative`);
        }
    }
};
function convertToMap(keys, values) {
    const r = {};
    for (let i = 0; i < keys.length; i++) {
        if (typeof values[i] !== typeof lib_base_1.Decimal.ZERO) {
            r[keys[i]] = _utils_1.decimalify(values[i]);
        }
        else {
            r[keys[i]] = values[i];
        }
    }
    return r;
}
function bntoDec(bigNums) {
    const decimals = [];
    for (let i = 0; i < bigNums.length; i++) {
        decimals.push(_utils_1.decimalify(bigNums[i]));
    }
    return decimals;
}
/**
 * Ethers-based implementation of {@link @liquity/lib-base#ReadableLiquity}.
 *
 * @public
 */
class ReadableEthersLiquity {
    /** @internal */
    constructor(connection) {
        this.connection = connection;
    }
    /** @internal */
    static _from(connection) {
        const readable = new ReadableEthersLiquity(connection);
        return connection.useStore === "blockPolled"
            ? new _BlockPolledReadableEthersLiquity(readable)
            : readable;
    }
    /**
     * Connect to the Liquity protocol and create a `ReadableEthersLiquity` object.
     *
     * @param signerOrProvider - Ethers `Signer` or `Provider` to use for connecting to the Ethereum
     *                           network.
     * @param optionalParams - Optional parameters that can be used to customize the connection.
     */
    static async connect(signerOrProvider, optionalParams) {
        return ReadableEthersLiquity._from(await EthersLiquityConnection_1._connect(signerOrProvider, optionalParams));
    }
    hasStore() {
        return false;
    }
    /** {@inheritDoc @liquity/lib-base#ReadableLiquity.getTotalRedistributed} */
    async getTotalRedistributed(overrides) {
        const { troveManager, yetiController } = EthersLiquityConnection_1._getContracts(this.connection);
        const { userAddress } = this.connection;
        const L_collAmounts = {};
        let totalDebt = lib_base_1.Decimal.ZERO;
        const [troveColls] = await Promise.all([troveManager.getTroveColls(userAddress !== null && userAddress !== void 0 ? userAddress : '0')]);
        const L_decimals = await this.getDecimals(troveColls[0]);
        for (let i = 0; i < troveColls[0].length; i++) {
            const [collateral, debt] = await Promise.all([
                troveManager.getL_Coll(troveColls[0][i], { ...overrides }).then(_utils_1.decimalify),
                troveManager.getL_YUSD(troveColls[0][i], { ...overrides }).then(_utils_1.decimalify),
            ]);
            L_collAmounts[troveColls[0][i]] = collateral;
            totalDebt = totalDebt.add(debt);
        }
        return new lib_base_1.Trove(L_collAmounts, L_decimals, { 'debt': totalDebt });
    }
    /** {@inheritDoc @liquity/lib-base#ReadableLiquity.getTroveBeforeRedistribution} */
    async getTroveBeforeRedistribution(address, overrides) {
        // address ??= _requireAddress(this.connection);
        // return new TroveWithPendingRedistribution(address, userTroveStatusFrom((0)));
        address !== null && address !== void 0 ? address : (address = EthersLiquityConnection_1._requireAddress(this.connection));
        const { troveManager, yetiController } = EthersLiquityConnection_1._getContracts(this.connection);
        const [troveStatus, troveDebt, troveColls] = await Promise.all([
            troveManager.getTroveStatus(address, { ...overrides }),
            troveManager.getTroveDebt(address, { ...overrides }),
            troveManager.getTroveColls(address, { ...overrides })
        ]);
        const collaterals = {};
        const decimals = await this.getDecimals(troveColls[0]);
        for (let i = 0; i < troveColls[1].length; i++) {
            collaterals[troveColls[0][i]] = _utils_1.decimalify(troveColls[1][i]);
        }
        const decimalDebt = _utils_1.decimalify(troveDebt);
        const debt = { 'debt': decimalDebt };
        const active = 1;
        const stakes = {};
        const snapshotAmounts = {};
        let snapshotYUSDDebt = bignumber_1.BigNumber.from('0');
        const totalSnapshotYUSDDebt = {};
        // Flatten promises 
        let promises = troveColls[0].map((coll) => { return troveManager.getTroveStake(address == undefined ? "" : address, coll); });
        const rewardColl = troveColls[0].map((coll) => { return troveManager.getRewardSnapshotColl(address == undefined ? "" : address, coll); });
        const getRewardSnapshotYUSD = troveColls[0].map((coll) => { return troveManager.getRewardSnapshotYUSD(address == undefined ? "" : address, coll); });
        promises = promises.concat(rewardColl);
        promises = promises.concat(getRewardSnapshotYUSD);
        const result = await Promise.all(promises);
        for (let i = 0; i < troveColls[0].length; i++) {
            stakes[troveColls[0][i]] = _utils_1.decimalify(result[i * 3]);
            snapshotAmounts[troveColls[0][i]] = _utils_1.decimalify(result[(i * 3) + 1]);
            snapshotYUSDDebt = snapshotYUSDDebt.add(result[(i * 3) + 2]);
        }
        // for (const key in collaterals) {
        //   const [stake, snapshotAmount, colYUSDDebt] = await Promise.all([
        //     troveManager.getTroveStake(address, key),
        //     troveManager.getRewardSnapshotColl(address, key),
        //     troveManager.getRewardSnapshotYUSD(address, key)
        //   ])
        //   stakes[key] = decimalify(stake)
        //   snapshotAmounts[key] = decimalify(snapshotAmount);
        //   snapshotYUSDDebt = snapshotYUSDDebt.add(colYUSDDebt);
        // }
        totalSnapshotYUSDDebt['debt'] = _utils_1.decimalify(snapshotYUSDDebt);
        if (_utils_1.numberify(troveStatus) == active) {
            return new lib_base_1.TroveWithPendingRedistribution(address, userTroveStatusFrom(_utils_1.numberify(troveStatus)), collaterals, decimals, debt, stakes, new lib_base_1.Trove(snapshotAmounts, decimals, totalSnapshotYUSDDebt));
        }
        else {
            return new lib_base_1.TroveWithPendingRedistribution(address, userTroveStatusFrom(_utils_1.numberify(troveStatus)));
        }
    }
    // ownerAddress: string,
    //   status: UserTroveStatus,
    //   collateral?: string[],
    //   collateralAmounts?: Decimal[],
    //   debt?: Decimal,
    //   stake = Decimal.ZERO,
    //   snapshotOfTotalRedistributed = _emptyTrove
    /** {@inheritDoc @liquity/lib-base#ReadableLiquity.getTrove} */
    async getTrove(address, overrides) {
        const [trove, totalRedistributed] = await Promise.all([
            this.getTroveBeforeRedistribution(address, overrides),
            this.getTotalRedistributed(overrides)
        ]);
        return trove.applyRedistribution(totalRedistributed);
    }
    /** {@inheritDoc @liquity/lib-base#ReadableLiquity.getNumberOfTroves} */
    async getNumberOfTroves(overrides) {
        const { troveManager } = EthersLiquityConnection_1._getContracts(this.connection);
        return (await troveManager.getTroveOwnersCount({ ...overrides })).toNumber();
    }
    // if the collateral is not wrapped, it returns Decimal(1)
    async getUnderlyingPerReceiptRatios(addresses, vaultTokens, overrides) {
        const ratios = {};
        for (let i = 0; i < addresses.length; i++) {
            const address = addresses[i];
            if (vaultTokens.includes(address)) {
                ratios[address] = await this.getUnderlyingPerReceipt(address);
            }
            else {
                ratios[address] = lib_base_1.Decimal.from(1);
            }
        }
        return ratios;
    }
    // if the collateral is not wrapped, it returns Decimal(1)
    async getReceiptPerUnderlyingRatios(addresses, vaultTokens, overrides) {
        const ratios = {};
        for (let i = 0; i < addresses.length; i++) {
            const address = addresses[i];
            if (vaultTokens.includes(address)) {
                ratios[address] = await this.getReceiptPerUnderlying(address);
            }
            else {
                ratios[address] = lib_base_1.Decimal.from(1);
            }
        }
        return ratios;
    }
    /** {@inheritDoc @liquity/lib-base#ReadableLiquity.getCollPrice} */
    getCollPrice(address, overrides) {
        const { yetiController } = EthersLiquityConnection_1._getContracts(this.connection);
        return yetiController.getPrice(address, { ...overrides }).then(_utils_1.decimalify);
    }
    async getPrices(addresses, overrides) {
        const { yetiController } = EthersLiquityConnection_1._getContracts(this.connection);
        const prices = {};
        const promises = addresses.map((coll) => { return yetiController.getPrice(coll); });
        // const vaultAddresses: string[] = []
        const result = await Promise.all(promises);
        for (let i = 0; i < addresses.length; i++) {
            prices[addresses[i]] = _utils_1.decimalify(result[i]);
        }
        return prices;
    }
    async getUnderlyingDecimals(vaultTokens, overrides) {
        const decimals = {};
        for (let i = 0; i < vaultTokens.length; i++) {
            const address = vaultTokens[i];
            decimals[address] = await this.getUnderlyingDecimal(address);
        }
        return decimals;
    }
    /** {@inheritDoc @liquity/lib-base#ReadableLiquity.getCollPrices} */
    async getCollPrices(addresses, vaultTokens, underlyingDecimals, overrides) {
        const { yetiController } = EthersLiquityConnection_1._getContracts(this.connection);
        const prices = {};
        const promises = addresses.map((coll) => { return yetiController.getPrice(coll); });
        // const vaultAddresses: string[] = []
        const result = await Promise.all(promises);
        // ToDo change it back
        for (let i = 0; i < addresses.length; i++) {
            prices[addresses[i]] = _utils_1.decimalify(result[i]);
        }
        // console.log("prices before", prices)
        for (let i = 0; i < vaultTokens.length; i++) {
            const address = vaultTokens[i];
            const ratioPrice = (await this.getReceiptPerUnderlying(address));
            // console.log("ratioPrice", ratioPrice)
            // console.log("vaultAddresses[i]", vaultAddresses[i])
            // console.log("prices", prices)
            // console.log("underlyingPrices before division", ratioPrice.mul(prices[vaultAddresses[i]]))
            prices[address] = ratioPrice.mul(prices[address]).div(lib_base_1.Decimal.from(10 ** (18 - +underlyingDecimals[address].bigNumber)));
            // console.log("priceAfterDivision", prices[vaultAddresses[i]])
        }
        return prices;
    }
    async getBalances(addresses, overrides) {
        const balances = {};
        const promises = addresses.map((coll) => { return this.getBalanceERC20(coll); });
        const result = await Promise.all(promises);
        for (let i = 0; i < addresses.length; i++) {
            balances[addresses[i]] = result[i];
        }
        return balances;
    }
    async getLPBalances(addresses, overrides) {
        addresses !== null && addresses !== void 0 ? addresses : (addresses = EthersLiquityConnection_1._requireAddress(this.connection));
        const { lpToken } = EthersLiquityConnection_1._getContracts(this.connection);
        const balance = lpToken.balanceOf(addresses).then(_utils_1.decimalify);
        return balance;
    }
    // /** {@inheritDoc @liquity/lib-base#ReadableLiquity.getPrice} */
    // // getPrice(overrides?: EthersCallOverrides): Promise<Decimal> {
    // //   const { priceFeed } = _getContracts(this.connection);
    // //   return priceFeed.callStatic.fetchPrice({ ...overrides }).then(decimalify);
    // // }
    /** @internal */
    async _getActivePool(overrides) {
        const { activePool } = EthersLiquityConnection_1._getContracts(this.connection);
        const [activeDebt] = await Promise.all([
            activePool.getYUSDDebt({ ...overrides })
        ].map(getBigNumber => getBigNumber.then(_utils_1.decimalify)));
        const [activeColls] = await Promise.all([activePool.getAllCollateral()]);
        const decColls = [];
        const decimals = await this.getDecimals(activeColls[0]);
        for (let i = 0; i < activeColls[1].length; i++) {
            decColls.push(_utils_1.decimalify(activeColls[1][i]));
        }
        const collaterals = convertToMap(activeColls[0], decColls);
        // for (const coll in whitelistedColls) {
        //   const [collAmount] = await Promise.all(
        //     [
        //       activePool.getCollateralVC(coll, { ...overrides })
        //     ].map(getBigNumber => getBigNumber.then(decimalify))
        //   );
        //   collaterals[coll] = collAmount;
        // }
        return new lib_base_1.Trove(collaterals, decimals, { 'debt': activeDebt });
    }
    /** @internal */
    async _getDefaultPool(overrides) {
        const { defaultPool } = EthersLiquityConnection_1._getContracts(this.connection);
        const [defaultDebt] = await Promise.all([
            defaultPool.getYUSDDebt({ ...overrides })
        ].map(getBigNumber => getBigNumber.then(_utils_1.decimalify)));
        const [defaultColls] = await Promise.all([defaultPool.getAllCollateral()]);
        const decColls = [];
        const decimals = await this.getDecimals(defaultColls[0]);
        for (let i = 0; i < defaultColls[1].length; i++) {
            decColls.push(_utils_1.decimalify(defaultColls[1][i]));
        }
        const collaterals = convertToMap(defaultColls[0], decColls);
        // for (const coll in whitelistedColls) {
        //   const [collAmount] = await Promise.all(
        //     [
        //       defaultPool.getCollateral(coll, { ...overrides })
        //     ].map(getBigNumber => getBigNumber.then(decimalify))
        //   );
        //   collaterals[coll] = collAmount;
        // }
        return new lib_base_1.Trove(collaterals, decimals, { 'debt': defaultDebt });
    }
    /** {@inheritDoc @liquity/lib-base#ReadableLiquity.getTotal} */
    async getTotal(overrides) {
        const [activePool, defaultPool] = await Promise.all([
            this._getActivePool(overrides),
            this._getDefaultPool(overrides)
        ]);
        // console.log('activePool', activePool)
        // console.log('defaultPool', defaultPool)
        return activePool.add(defaultPool);
    }
    /** {@inheritDoc @liquity/lib-base#ReadableLiquity.getStabilityDeposit} */
    async getStabilityDeposit(address, overrides) {
        address !== null && address !== void 0 ? address : (address = EthersLiquityConnection_1._requireAddress(this.connection));
        const { stabilityPool } = EthersLiquityConnection_1._getContracts(this.connection);
        const [initialValue, currentYUSD, collateralsGain, yetiReward] = await Promise.all([
            stabilityPool.deposits(address, { ...overrides }),
            stabilityPool.getCompoundedYUSDDeposit(address, { ...overrides }),
            stabilityPool.getDepositorGains(address, { ...overrides }),
            stabilityPool.getDepositorYETIGain(address, { ...overrides })
        ]);
        const collGain = convertToMap(collateralsGain[0], bntoDec(collateralsGain[1]));
        return new lib_base_1.StabilityDeposit(_utils_1.decimalify(initialValue), _utils_1.decimalify(currentYUSD), collGain, _utils_1.decimalify(yetiReward));
    }
    async getFarm(address, overrides) {
        address !== null && address !== void 0 ? address : (address = EthersLiquityConnection_1._requireAddress(this.connection));
        const { farm } = EthersLiquityConnection_1._getContracts(this.connection);
        const [lpTokenBalance, earnedYETI, totalLPStaked, rewardRate,] = await Promise.all([
            farm.balanceOf(address, { ...overrides }),
            farm.earned(address, { ...overrides }),
            farm.totalSupply({ ...overrides }),
            farm.rewardRate({ ...overrides })
        ]);
        return new lib_base_1.Farm(_utils_1.decimalify(lpTokenBalance), _utils_1.decimalify(earnedYETI), _utils_1.decimalify(totalLPStaked), _utils_1.decimalify(rewardRate));
    }
    async getBoostedFarm(address, overrides) {
        address !== null && address !== void 0 ? address : (address = EthersLiquityConnection_1._requireAddress(this.connection));
        const { boostedFarm } = EthersLiquityConnection_1._getContracts(this.connection);
        const [lpTokenBalance, earnedYETI, totalLPStaked, rewardRate,] = await Promise.all([
            boostedFarm.userInfo(address, { ...overrides }),
            boostedFarm.pendingTokens(address, { ...overrides }),
            boostedFarm.totalSupply({ ...overrides }),
            boostedFarm.rewardRate({ ...overrides })
        ]);
        return new lib_base_1.Farm(_utils_1.decimalify(lpTokenBalance.amount), _utils_1.decimalify(earnedYETI), _utils_1.decimalify(totalLPStaked), _utils_1.decimalify(rewardRate));
    }
    async getVEYETIStake(address, overrides) {
        address !== null && address !== void 0 ? address : (address = EthersLiquityConnection_1._requireAddress(this.connection));
        const { veYETI, veYETIEmissions, boostedFarm } = EthersLiquityConnection_1._getContracts(this.connection);
        const boostUserAmount = await boostedFarm.userInfo(address, { ...overrides });
        const [yetiStake, yetiStakeOnFarm, veYetiOnFarm, veYETIGain, veYETITotal, totalUserYeti, totalYeti, yetiEarned, veYETIEmisionsRewardRate, accumulationRate, boostRewardRate, boostBasePartition, boostSumOfFactors, boostTotalSupply] = await Promise.all([
            veYETI.getUserYetiOnRewarder(address, '0x0000000000000000000000000000000000000000', { ...overrides }),
            veYETI.getUserYetiOnRewarder(address, '0xD8A4AA01D54C8Fdd104EAC28B9C975f0663E75D8', { ...overrides }),
            veYETI.getVeYetiOnRewarder(address, '0xD8A4AA01D54C8Fdd104EAC28B9C975f0663E75D8', { ...overrides }),
            veYETI.getVeYetiOnRewarder(address, '0x0000000000000000000000000000000000000000', { ...overrides }),
            veYETI.getTotalVeYeti(address, { ...overrides }),
            veYETI.getTotalYeti(address, { ...overrides }),
            veYETI.totalYeti({ ...overrides }),
            veYETIEmissions.earned(address, { ...overrides }),
            veYETIEmissions.rewardRate({ ...overrides }),
            veYETI.getAccumulationRate({ ...overrides }),
            boostedFarm.rewardRate({ ...overrides }),
            boostedFarm.basePartition({ ...overrides }),
            boostedFarm.sumOfFactors({ ...overrides }),
            boostedFarm.totalSupply({ ...overrides })
        ]);
        return new lib_base_1.veYETIStake(_utils_1.decimalify(yetiStake), _utils_1.decimalify(veYETIGain), _utils_1.decimalify(veYETITotal), _utils_1.decimalify(totalUserYeti), _utils_1.decimalify(totalYeti), _utils_1.decimalify(yetiEarned), _utils_1.decimalify(veYETIEmisionsRewardRate), _utils_1.decimalify(accumulationRate), _utils_1.decimalify(boostUserAmount.amount), _utils_1.decimalify(boostUserAmount.factor), _utils_1.decimalify(boostRewardRate), _utils_1.decimalify(boostBasePartition), _utils_1.decimalify(yetiStakeOnFarm), _utils_1.decimalify(boostSumOfFactors), _utils_1.decimalify(veYetiOnFarm), _utils_1.decimalify(boostTotalSupply));
    }
    /** {@inheritDoc @liquity/lib-base#ReadableLiquity.getRemainingStabilityPoolYETIReward} */
    async getPoolRewardRate(overrides) {
        const { communityIssuance } = EthersLiquityConnection_1._getContracts(this.connection);
        const rewardRate = _utils_1.decimalify(await communityIssuance.getRewardRate({ ...overrides }));
        return rewardRate;
    }
    /** {@inheritDoc @liquity/lib-base#ReadableLiquity.getRemainingStabilityPoolYETIReward} */
    async getRemainingStabilityPoolYETIReward(overrides) {
        const { communityIssuance } = EthersLiquityConnection_1._getContracts(this.connection);
        const issuanceCap = this.connection.totalStabilityPoolYETIReward;
        //TODO Yeti
        const totalYETIIssued = _utils_1.decimalify(await communityIssuance.totalYetiIssued({ ...overrides }));
        const burnedRewards = 18000000;
        // totalYETIIssued approaches but never reaches issuanceCap
        return issuanceCap.sub(totalYETIIssued).sub(burnedRewards);
    }
    /** {@inheritDoc @liquity/lib-base#ReadableLiquity.getYUSDInStabilityPool} */
    getYUSDInStabilityPool(overrides) {
        const { stabilityPool } = EthersLiquityConnection_1._getContracts(this.connection);
        return stabilityPool.getTotalYUSDDeposits({ ...overrides }).then(_utils_1.decimalify);
    }
    // Get Deposit fee for an individual collateral helper function.
    async getDepositFee(VCInput, VCOutput, overrides) {
        const { borrowerOperations, yetiController, activePool, defaultPool } = EthersLiquityConnection_1._getContracts(this.connection);
        const [totalVCBalancePre] = await Promise.all([borrowerOperations.getEntireSystemColl()]);
        const allVCDifference = bignumber_1.BigNumber.from('0');
        const totalFee = {};
        for (const coll in VCInput) {
            const [collateralVCInput] = await Promise.all([yetiController.getValueVC(coll, VCInput[coll].hex)]);
            allVCDifference.add(collateralVCInput);
        }
        for (const coll in VCOutput) {
            const [collateralVCInput] = await Promise.all([yetiController.getValueVC(coll, VCOutput[coll].hex)]);
            allVCDifference.sub(collateralVCInput);
            totalFee[coll] = lib_base_1.Decimal.ZERO;
        }
        for (const coll in VCInput) {
            // console.log('VCInput[coll].hex', coll, VCInput[coll].hex)
            const [collateralVCInput, activePoolVCBalance, defaultPoolVCBalance] = await Promise.all([
                (await yetiController.getValueVC(coll, VCInput[coll].hex)),
                activePool.getCollateralVC(coll),
                defaultPool.getCollateralVC(coll),
            ]);
            if (collateralVCInput.eq(bignumber_1.BigNumber.from('0'))) {
                totalFee[coll] = lib_base_1.Decimal.ZERO;
            }
            else {
                // console.log('111', collateralVCInput)
                // console.log('222', activePoolVCBalance.add(defaultPoolVCBalance))
                // console.log('333', totalVCBalancePre)
                // console.log('444', totalVCBalancePre.add(allVCDifference))
                const [depositFee] = await Promise.all([
                    yetiController.getVariableDepositFee(coll, collateralVCInput, activePoolVCBalance.add(defaultPoolVCBalance), totalVCBalancePre, totalVCBalancePre.add(allVCDifference))
                ]);
                totalFee[coll] = _utils_1.decimalify(depositFee);
            }
        }
        return totalFee;
    }
    // async getRiskiestTrove(overrides?: EthersCallOverrides): Promise<TroveWithPendingRedistribution> {
    //   const { sortedTroves } = _getContracts(this.connection)
    //   const [numberOfTroves] = await Promise.all([this.getNumberOfTroves()])
    //   if (numberOfTroves === 0) {
    //     return new TroveWithPendingRedistribution('0x0000000000000000000000000000000000000000', "nonExistent");
    //   } else {
    //     const [address] =  await Promise.all([sortedTroves.getLast()])
    //     return this.getTroveBeforeRedistribution(address)
    //   }
    // }
    // Get estimated YETI rewards from farming Curve LP tokens
    async getEstimatedFarmRewards(amount, time, overrides) {
        const { farm } = EthersLiquityConnection_1._getContracts(this.connection);
        const estimatedRewards = await farm.rewardToEarn(lib_base_1.Decimal.from(String(amount)).hex, lib_base_1.Decimal.from(String(time)).hex);
        return _utils_1.decimalify(estimatedRewards).div(lib_base_1.Decimal.from(1000000000000000000));
    }
    async getEstimatedVeYetiRewards(amount, time, overrides) {
        const { veYETIEmissions } = EthersLiquityConnection_1._getContracts(this.connection);
        const estimatedRewards = await veYETIEmissions.rewardToEarn(lib_base_1.Decimal.from(String(amount)).hex, lib_base_1.Decimal.from(String(time)).hex);
        return _utils_1.decimalify(estimatedRewards).div(lib_base_1.Decimal.from(1000000000000000000));
    }
    async getEstimatedYETIPoolRewards(amount, time, overrides) {
        const { stabilityPool } = EthersLiquityConnection_1._getContracts(this.connection);
        const estimatedRewards = await stabilityPool.getEstimatedYETIPoolRewards(lib_base_1.Decimal.from(String(amount)).hex, lib_base_1.Decimal.from(String(time)).hex);
        return _utils_1.decimalify(estimatedRewards).div(lib_base_1.Decimal.from(1000000000000000000));
    }
    async getBalanceERC20(address) {
        const signer = EthersLiquityConnection_1._requireSigner(this.connection);
        const userAddress = EthersLiquityConnection_1._requireAddress(this.connection);
        if (address != undefined) {
            const erc20 = contracts_1._getERC20Token(address, signer);
            const balance = erc20.token.balanceOf(userAddress).then(_utils_1.decimalify);
            return balance;
        }
        return lib_base_1.Decimal.ZERO;
    }
    async getLP(address) {
        const signer = EthersLiquityConnection_1._requireSigner(this.connection);
        const userAddress = EthersLiquityConnection_1._requireAddress(this.connection);
        if (address != undefined) {
            const erc20 = contracts_1._getLPToken(address, signer);
            const balance = erc20.token.balanceOf(userAddress).then(_utils_1.decimalify);
        }
        return lib_base_1.Decimal.ZERO;
    }
    async getSortedTroveHead() {
        const { sortedTroves } = EthersLiquityConnection_1._getContracts(this.connection);
        return await sortedTroves.getFirst();
    }
    async getSortedTroveTail() {
        const { sortedTroves } = EthersLiquityConnection_1._getContracts(this.connection);
        return await sortedTroves.getLast();
    }
    async getSortedTroveSize() {
        const { sortedTroves } = EthersLiquityConnection_1._getContracts(this.connection);
        return (await sortedTroves.getSize()).toNumber();
    }
    async getSortedTroveNext(id) {
        const { sortedTroves } = EthersLiquityConnection_1._getContracts(this.connection);
        return await sortedTroves.getNext(id);
    }
    async getSortedTrovePrev(id) {
        const { sortedTroves } = EthersLiquityConnection_1._getContracts(this.connection);
        return await sortedTroves.getPrev(id);
    }
    async getCurrentAICR(borrower) {
        const { troveManager } = EthersLiquityConnection_1._getContracts(this.connection);
        return troveManager.getCurrentAICR(borrower).then(_utils_1.decimalify);
    }
    async getTroveDebt(borrower) {
        const { troveManager } = EthersLiquityConnection_1._getContracts(this.connection);
        return troveManager.getTroveDebt(borrower).then(_utils_1.decimalify);
    }
    async getYusdAllowance(from, to, amount) {
        const { yusdToken } = EthersLiquityConnection_1._getContracts(this.connection);
        let result = false;
        const allowanceResult = await yusdToken.allowance(from, to);
        if (_utils_1.decimalify(allowanceResult).gte(amount)) {
            result = true;
        }
        return result;
    }
    async getYETIPrice() {
        const { priceFeed } = EthersLiquityConnection_1._getContracts(this.connection);
        return priceFeed.fetchPrice_v().then(_utils_1.decimalify);
    }
    async getYUSDPrice() {
        const { YUSDPriceFeed } = EthersLiquityConnection_1._getContracts(this.connection);
        return YUSDPriceFeed.fetchPrice_v().then(_utils_1.decimalify);
    }
    async getGlobalBoostFactor() {
        const { sortedTroves } = EthersLiquityConnection_1._getContracts(this.connection);
        return sortedTroves.globalBoostFactor().then(_utils_1.decimalify);
    }
    async getDecayedBoost(address) {
        address !== null && address !== void 0 ? address : (address = EthersLiquityConnection_1._requireAddress(this.connection));
        const { sortedTroves } = EthersLiquityConnection_1._getContracts(this.connection);
        return sortedTroves.getDecayedBoost(address).then(_utils_1.decimalify);
    }
    async hasClaimableCollateral(address, overrides) {
        const { collSurplusPool } = EthersLiquityConnection_1._getContracts(this.connection);
        return collSurplusPool.hasClaimableCollateral(address);
    }
    async getRedemptionBonus(address, overrides) {
        const { collSurplusPool } = EthersLiquityConnection_1._getContracts(this.connection);
        return collSurplusPool.getRedemptionBonus(address).then(_utils_1.decimalify);
    }
    async getRedemptionFeeRate(overrides) {
        const { troveManager } = EthersLiquityConnection_1._getContracts(this.connection);
        return troveManager.getRedemptionRateWithDecay().then(_utils_1.decimalify);
    }
    async getReceiptPerUnderlying(address) {
        const signer = EthersLiquityConnection_1._requireSigner(this.connection);
        const vault = contracts_1._getYetiVaultToken(address, signer);
        return vault.vault.receiptPerUnderlying().then(_utils_1.decimalify);
    }
    async getUnderlyingPerReceipt(address) {
        const signer = EthersLiquityConnection_1._requireSigner(this.connection);
        const vault = contracts_1._getYetiVaultToken(address, signer);
        return vault.vault.underlyingPerReceipt().then(_utils_1.decimalify);
    }
    async getUnderlyingDecimal(address) {
        const signer = EthersLiquityConnection_1._requireSigner(this.connection);
        const vault = contracts_1._getYetiVaultToken(address, signer);
        return vault.vault.underlyingDecimal().then(_utils_1.decimalify);
    }
    async getUnderlyingToken(address) {
        const signer = EthersLiquityConnection_1._requireSigner(this.connection);
        const vault = contracts_1._getYetiVaultToken(address, signer);
        return vault.vault.underlying();
    }
    async getAllowanceOf(owner, token, spender, amount) {
        const signer = EthersLiquityConnection_1._requireSigner(this.connection);
        let enoughAllowance = false;
        if (owner != undefined && spender != undefined) {
            // console.log("danger zone")
            const erc20 = contracts_1._getERC20Token(token, signer);
            const result = await erc20.token.allowance(owner, spender);
            // console.log("danger zone result", result)
            // console.log("danger zone result", amount)
            // console.log("decimalify(amount", decimalify(result))
            // console.log("CHECK", decimalify(result) >= amount)
            if (_utils_1.decimalify(result).gte(amount)) {
                // console.log("hit")
                enoughAllowance = true;
            }
        }
        return enoughAllowance;
    }
    // async getReceiptPerUnderlying(): Primise<Decimal> {
    //   const {signer} 
    // }
    /** {@inheritDoc @liquity/lib-base#ReadableLiquity.getYUSDBalance} */
    getYUSDBalance(address, overrides) {
        address !== null && address !== void 0 ? address : (address = EthersLiquityConnection_1._requireAddress(this.connection));
        const { yusdToken } = EthersLiquityConnection_1._getContracts(this.connection);
        const balance = yusdToken.balanceOf(address, { ...overrides }).then(_utils_1.decimalify);
        return balance;
    }
    /** {@inheritDoc @liquity/lib-base#ReadableLiquity.getYETIBalance} */
    getYETIBalance(address, overrides) {
        address !== null && address !== void 0 ? address : (address = EthersLiquityConnection_1._requireAddress(this.connection));
        const { yetiToken } = EthersLiquityConnection_1._getContracts(this.connection);
        const balance = yetiToken.balanceOf(address, { ...overrides }).then(_utils_1.decimalify);
        return balance;
    }
    /** {@inheritDoc @liquity/lib-base#ReadableLiquity.getUniTokenBalance} */
    // getUniTokenBalance(address?: string, overrides?: EthersCallOverrides): Promise<Decimal> {
    //   address ??= _requireAddress(this.connection);
    //   const { uniToken } = _getContracts(this.connection);
    //   return uniToken.balanceOf(address, { ...overrides }).then(decimalify);
    // }
    /** {@inheritDoc @liquity/lib-base#ReadableLiquity.pngGetUniTokenBalance} */
    // pngGetUniTokenBalance(address?: string, overrides?: EthersCallOverrides): Promise<Decimal> {
    //   address ??= _requireAddress(this.connection);
    //   const { pngToken } = _getContracts(this.connection);
    //   return pngToken.balanceOf(address, { ...overrides }).then(decimalify);
    // }
    /** {@inheritDoc @liquity/lib-base#ReadableLiquity.tjGetUniTokenBalance} */
    // tjGetUniTokenBalance(address?: string, overrides?: EthersCallOverrides): Promise<Decimal> {
    //   address ??= _requireAddress(this.connection);
    //   const { tjToken } = _getContracts(this.connection);
    //   return tjToken.balanceOf(address, { ...overrides }).then(decimalify);
    // }
    /** {@inheritDoc @liquity/lib-base#ReadableLiquity.getUniTokenAllowance} */
    // getUniTokenAllowance(address?: string, overrides?: EthersCallOverrides): Promise<Decimal> {
    //   address ??= _requireAddress(this.connection);
    //   const { uniToken, unipool } = _getContracts(this.connection);
    //   return uniToken.allowance(address, unipool.address, { ...overrides }).then(decimalify);
    // }
    /** {@inheritDoc @liquity/lib-base#ReadableLiquity.pngGetUniTokenAllowance} */
    // pngGetUniTokenAllowance(address?: string, overrides?: EthersCallOverrides): Promise<Decimal> {
    //   address ??= _requireAddress(this.connection);
    //   const { pngToken, pngUnipool } = _getContracts(this.connection);
    //   return pngToken.allowance(address, pngUnipool.address, { ...overrides }).then(decimalify);
    // }
    /** {@inheritDoc @liquity/lib-base#ReadableLiquity.tjGetUniTokenBalance} */
    // tjGetUniTokenAllowance(address?: string, overrides?: EthersCallOverrides): Promise<Decimal> {
    //   address ??= _requireAddress(this.connection);
    //   const { tjToken, tjUnipool } = _getContracts(this.connection);
    //   return tjToken.allowance(address, tjUnipool.address, { ...overrides }).then(decimalify);
    // }
    /** @internal */
    // async _getRemainingLiquidityMiningYETIRewardCalculator(
    //   overrides?: EthersCallOverrides
    // ): Promise<(blockTimestamp: number) => Decimal> {
    //   const { unipool } = _getContracts(this.connection);
    //   const [totalSupply, rewardRate, periodFinish, lastUpdateTime] = await Promise.all([
    //     unipool.totalSupply({ ...overrides }),
    //     unipool.rewardRate({ ...overrides }).then(decimalify),
    //     unipool.periodFinish({ ...overrides }).then(numberify),
    //     unipool.lastUpdateTime({ ...overrides }).then(numberify)
    //   ]);
    //   return (blockTimestamp: number) => {
    //     return rewardRate.mul(
    //       Math.max(0, periodFinish - (totalSupply.isZero() ? lastUpdateTime : blockTimestamp))
    //     );
    //   };
    // }
    /** @internal */
    // async _pngGetRemainingLiquidityMiningYETIRewardCalculator(
    //   overrides?: EthersCallOverrides
    // ): Promise<(blockTimestamp: number) => Decimal> {
    //   const { pngUnipool: unipool } = _getContracts(this.connection);
    //   const [totalSupply, rewardRate, periodFinish, lastUpdateTime] = await Promise.all([
    //     unipool.totalSupply({ ...overrides }),
    //     unipool.rewardRate({ ...overrides }).then(decimalify),
    //     unipool.periodFinish({ ...overrides }).then(numberify),
    //     unipool.lastUpdateTime({ ...overrides }).then(numberify)
    //   ]);
    //   return (blockTimestamp: number) => {
    //     return rewardRate.mul(
    //       Math.max(0, periodFinish - (totalSupply.isZero() ? lastUpdateTime : blockTimestamp))
    //     );
    //   };
    // }
    /** {@inheritDoc @liquity/lib-base#ReadableLiquity.getRemainingLiquidityMiningYETIReward} */
    // async getRemainingLiquidityMiningYETIReward(overrides?: EthersCallOverrides): Promise<Decimal> {
    //   const [calculateRemainingYETI, blockTimestamp] = await Promise.all([
    //     this._getRemainingLiquidityMiningYETIRewardCalculator(overrides),
    //     this._getBlockTimestamp(overrides?.blockTag)
    //   ]);
    //   return calculateRemainingYETI(blockTimestamp);
    // }
    /** {@inheritDoc @liquity/lib-base#ReadableLiquity.pngGetRemainingLiquidityMiningYETIReward} */
    // async pngGetRemainingLiquidityMiningYETIReward(overrides?: EthersCallOverrides): Promise<Decimal> {
    //   const [calculateRemainingYETI, blockTimestamp] = await Promise.all([
    //     this._pngGetRemainingLiquidityMiningYETIRewardCalculator(overrides),
    //     this._getBlockTimestamp(overrides?.blockTag)
    //   ]);
    //   return calculateRemainingYETI(blockTimestamp);
    // }
    /** @internal */
    // async _tjGetRemainingLiquidityMiningYETIRewardCalculator(
    //   overrides?: EthersCallOverrides
    // ): Promise<(blockTimestamp: number) => Decimal> {
    //   const { tjUnipool } = _getContracts(this.connection);
    //   const [totalSupply, rewardRate, periodFinish, lastUpdateTime] = await Promise.all([
    //     tjUnipool.totalSupply({ ...overrides }),
    //     tjUnipool.rewardRate({ ...overrides }).then(decimalify),
    //     tjUnipool.periodFinish({ ...overrides }).then(numberify),
    //     tjUnipool.lastUpdateTime({ ...overrides }).then(numberify)
    //   ]);
    //   return (blockTimestamp: number) =>
    //     rewardRate.mul(
    //       Math.max(0, periodFinish - (totalSupply.isZero() ? lastUpdateTime : blockTimestamp))
    //     );
    // }
    /** {@inheritDoc @liquity/lib-base#ReadableLiquity.getRemainingLiquidityMiningYETIReward} */
    // async tjGetRemainingLiquidityMiningYETIReward(overrides?: EthersCallOverrides): Promise<Decimal> {
    //   const [calculateRemainingYETI, blockTimestamp] = await Promise.all([
    //     this._tjGetRemainingLiquidityMiningYETIRewardCalculator(overrides),
    //     this._getBlockTimestamp(overrides?.blockTag)
    //   ]);
    //   return calculateRemainingYETI(blockTimestamp);
    // }
    /** {@inheritDoc @liquity/lib-base#ReadableLiquity.getLiquidityMiningStake} */
    // getLiquidityMiningStake(address?: string, overrides?: EthersCallOverrides): Promise<Decimal> {
    //   address ??= _requireAddress(this.connection);
    //   const { unipool } = _getContracts(this.connection);
    //   return unipool.balanceOf(address, { ...overrides }).then(decimalify);
    // }
    /** {@inheritDoc @liquity/lib-base#ReadableLiquity.pngGetLiquidityMiningStake} */
    // pngGetLiquidityMiningStake(address?: string, overrides?: EthersCallOverrides): Promise<Decimal> {
    //   address ??= _requireAddress(this.connection);
    //   const { pngUnipool: unipool } = _getContracts(this.connection);
    //   return unipool.balanceOf(address, { ...overrides }).then(decimalify);
    // }
    /** {@inheritDoc @liquity/lib-base#ReadableLiquity.tjGetLiquidityMiningStake} */
    // tjGetLiquidityMiningStake(address?: string, overrides?: EthersCallOverrides): Promise<Decimal> {
    //   address ??= _requireAddress(this.connection);
    //   const { tjUnipool } = _getContracts(this.connection);
    //   return tjUnipool.balanceOf(address, { ...overrides }).then(decimalify);
    // }
    /** {@inheritDoc @liquity/lib-base#ReadableLiquity.getTotalStakedUniTokens} */
    // getTotalStakedUniTokens(overrides?: EthersCallOverrides): Promise<Decimal> {
    //   const { unipool } = _getContracts(this.connection);
    //   return unipool.totalSupply({ ...overrides }).then(decimalify);
    // }
    /** {@inheritDoc @liquity/lib-base#ReadableLiquity.pngGetTotalStakedUniTokens} */
    // pngGetTotalStakedUniTokens(overrides?: EthersCallOverrides): Promise<Decimal> {
    //   const { pngUnipool: unipool } = _getContracts(this.connection);
    //   return unipool.totalSupply({ ...overrides }).then(decimalify);
    // }
    /** {@inheritDoc @liquity/lib-base#ReadableLiquity.tjGetTotalStakedUniTokens} */
    // tjGetTotalStakedUniTokens(overrides?: EthersCallOverrides): Promise<Decimal> {
    //   const { tjUnipool } = _getContracts(this.connection);
    //   return tjUnipool.totalSupply({ ...overrides }).then(decimalify);
    // }
    /** {@inheritDoc @liquity/lib-base#ReadableLiquity.getLiquidityMiningYETIReward} */
    // getLiquidityMiningYETIReward(address?: string, overrides?: EthersCallOverrides): Promise<Decimal> {
    //   address ??= _requireAddress(this.connection);
    //   const { unipool } = _getContracts(this.connection);
    //   return unipool.earned(address, { ...overrides }).then(decimalify);
    // }
    /** {@inheritDoc @liquity/lib-base#ReadableLiquity.pngGetLiquidityMiningYETIReward} */
    // pngGetLiquidityMiningYETIReward(
    //   address?: string,
    //   overrides?: EthersCallOverrides
    // ): Promise<Decimal> {
    //   address ??= _requireAddress(this.connection);
    //   const { pngUnipool: unipool } = _getContracts(this.connection);
    //   return unipool.earned(address, { ...overrides }).then(decimalify);
    // }
    /** {@inheritDoc @liquity/lib-base#ReadableLiquity.getLiquidityMiningYETIReward} */
    // tjGetLiquidityMiningYETIReward(
    //   address?: string,
    //   overrides?: EthersCallOverrides
    // ): Promise<Decimal> {
    //   address ??= _requireAddress(this.connection);
    //   const { tjUnipool } = _getContracts(this.connection);
    //   return tjUnipool.earned(address, { ...overrides }).then(decimalify);
    // }
    /** {@inheritDoc @liquity/lib-base#ReadableLiquity.getCollateralSurplusBalance} */
    async getCollateralSurplusBalancee(overrides) {
        const { collSurplusPool } = EthersLiquityConnection_1._getContracts(this.connection);
        const { userAddress } = this.connection;
        const surplusBalance = {};
        if (userAddress != undefined) {
            const [surplusColls] = await Promise.all([collSurplusPool.getAmountsClaimable(userAddress, overrides)]);
            for (let i = 0; i < surplusColls[0].length; i++) {
                surplusBalance[surplusColls[0][i]] = _utils_1.decimalify(surplusColls[1][i]);
            }
        }
        return surplusBalance;
        // OLD METHOD
        // const [surplusColls] = await Promise.all([collSurplusPool.getAllCollateral(overrides)]);
        // const surplusBalance: TroveMappings = {}
        // for (let i = 0; i < surplusColls[0].length; i++) {
        //   const [collEarned] = await Promise.all([collSurplusPool.getCollateral(surplusColls[0][i])]);
        //   surplusBalance[surplusColls[0][i]] = decimalify(collEarned)
        // }
        // return surplusBalance;
    }
    async getTroves(params, overrides) {
        var _a, _b;
        const { multiTroveGetter } = EthersLiquityConnection_1._getContracts(this.connection);
        expectPositiveInt(params, "first");
        expectPositiveInt(params, "startingAt");
        if (!validSortingOptions.includes(params.sortedBy)) {
            throw new Error(`sortedBy must be one of: ${validSortingOptions.map(x => `"${x}"`).join(", ")}`);
        }
        const [totalRedistributed, backendTroves] = await Promise.all([
            params.beforeRedistribution ? undefined : this.getTotalRedistributed({ ...overrides }),
            multiTroveGetter.getMultipleSortedTroves(params.sortedBy === "descendingCollateralRatio"
                ? (_a = params.startingAt) !== null && _a !== void 0 ? _a : 0 : -(((_b = params.startingAt) !== null && _b !== void 0 ? _b : 0) + 1), params.first, { ...overrides })
        ]);
        const decimals = await this.getDecimals(await this.getWhitelistedCollaterals());
        const troves = mapBackendTroves(backendTroves, decimals);
        if (totalRedistributed) {
            return troves.map(trove => trove.applyRedistribution(totalRedistributed));
        }
        else {
            return troves;
        }
    }
    /** @internal */
    _getBlockTimestamp(blockTag) {
        return EthersLiquityConnection_1._getBlockTimestamp(this.connection, blockTag);
    }
    /** @internal */
    async _getFeesFactory(overrides) {
        const { troveManager } = EthersLiquityConnection_1._getContracts(this.connection);
        const [lastFeeOperationTime, baseRateWithoutDecay] = await Promise.all([
            troveManager.lastFeeOperationTime({ ...overrides }),
            troveManager.baseRate({ ...overrides }).then(_utils_1.decimalify)
        ]);
        return (blockTimestamp, recoveryMode) => new lib_base_1.Fees(baseRateWithoutDecay, MINUTE_DECAY_FACTOR, BETA, convertToDate(lastFeeOperationTime.toNumber()), convertToDate(blockTimestamp), recoveryMode);
    }
    /** {@inheritDoc @liquity/lib-base#ReadableLiquity.getFees} */
    async getFees(overrides) {
        const [createFees, total, blockTimestamp] = await Promise.all([
            this._getFeesFactory(overrides),
            this.getTotal(overrides),
            this._getBlockTimestamp(overrides === null || overrides === void 0 ? void 0 : overrides.blockTag)
        ]);
        const recoveryRatios = await this.getRecoveryRatios(Object.keys(total.collaterals));
        const priceMapping = {};
        for (const key in Object.keys(total.collaterals)) {
            const [colPrice] = await Promise.all([this.getCollPrice(key)]);
            priceMapping[key] = colPrice;
        }
        return createFees(blockTimestamp, total.collateralRatioIsBelowCritical(priceMapping, recoveryRatios));
    }
    async getUnderlyingTokens(vaultTokens, overrides) {
        const underlyingTokens = [];
        for (let i = 0; i < vaultTokens.length; i++) {
            try {
                underlyingTokens.push(await this.getUnderlyingToken(vaultTokens[i]));
            }
            catch {
                console.log(vaultTokens[i], "not a vault token");
            }
        }
        return underlyingTokens;
    }
    async getVaultTokens(whitelistedCollaterals, overrides) {
        const { yetiController } = EthersLiquityConnection_1._getContracts(this.connection);
        const vaultTokens = [];
        for (let i = 0; i < whitelistedCollaterals.length; i++) {
            // console.log(whitelistedCollaterals[i], )
            if (await yetiController.isWrapped(whitelistedCollaterals[i], { ...overrides })) {
                // console.log(whitelistedCollaterals[i], true)
                vaultTokens.push(whitelistedCollaterals[i]);
            }
            // console.log(whitelistedCollaterals[i], false)
        }
        return vaultTokens;
    }
    async getWhitelistedCollaterals(overrides) {
        const { yetiController } = EthersLiquityConnection_1._getContracts(this.connection);
        return yetiController.getValidCollateral({ ...overrides });
    }
    async getICR(userAddress, overrides) {
        userAddress !== null && userAddress !== void 0 ? userAddress : (userAddress = EthersLiquityConnection_1._requireAddress(this.connection));
        const { troveManager } = EthersLiquityConnection_1._getContracts(this.connection);
        return troveManager.getCurrentICR(userAddress, { ...overrides }).then(_utils_1.decimalify);
    }
    async getVcValue(userAddress, overrides) {
        userAddress !== null && userAddress !== void 0 ? userAddress : (userAddress = EthersLiquityConnection_1._requireAddress(this.connection));
        const { troveManager } = EthersLiquityConnection_1._getContracts(this.connection);
        return troveManager.getTroveVC(userAddress, { ...overrides }).then(_utils_1.decimalify);
    }
    async getSafetyRatios(addresses, overrides) {
        const { yetiController } = EthersLiquityConnection_1._getContracts(this.connection);
        const safetyRatios = {};
        const promises = addresses.map((coll) => { return yetiController.getSafetyRatio(coll); });
        const result = await Promise.all(promises);
        for (let i = 0; i < addresses.length; i++) {
            safetyRatios[addresses[i]] = _utils_1.decimalify(result[i]);
        }
        return safetyRatios;
    }
    async getRecoveryRatios(addresses, overrides) {
        const { yetiController } = EthersLiquityConnection_1._getContracts(this.connection);
        const recoveryRatios = {};
        const promises = addresses.map((coll) => { return yetiController.getRecoveryRatio(coll); });
        const result = await Promise.all(promises);
        for (let i = 0; i < addresses.length; i++) {
            recoveryRatios[addresses[i]] = _utils_1.decimalify(result[i]);
        }
        return recoveryRatios;
    }
    async getDecimals(addresses, overrides) {
        const { yetiController } = EthersLiquityConnection_1._getContracts(this.connection);
        const decimals = {};
        const promises = addresses.map((coll) => { return yetiController.getDecimals(coll); });
        const result = await Promise.all(promises);
        for (let i = 0; i < addresses.length; i++) {
            decimals[addresses[i]] = _utils_1.decimalify(result[i]);
        }
        return decimals;
    }
}
exports.ReadableEthersLiquity = ReadableEthersLiquity;
const mapBackendTroves = (troves, decimals) => troves.map(trove => new lib_base_1.TroveWithPendingRedistribution(trove.owner, "open", // These Troves are coming from the SortedTroves list, so they must be open
convertToMap(trove.colls, trove.amounts), decimals, { 'debt': _utils_1.decimalify(trove.debt) }, convertToMap(trove.allColls, trove.stakeAmounts), 
//check whether we should return total snapshot YUSD debt instead of per collateral
new lib_base_1.Trove(convertToMap(trove.allColls, trove.snapshotAmounts), decimals, convertToMap(trove.allColls, trove.snapshotYUSDDebts))));
class _BlockPolledReadableEthersLiquity {
    constructor(readable) {
        const store = new BlockPolledLiquityStore_1.BlockPolledLiquityStore(readable);
        this.store = store;
        this.connection = readable.connection;
        this._readable = readable;
    }
    _blockHit(overrides) {
        return (!overrides ||
            overrides.blockTag === undefined ||
            overrides.blockTag === this.store.state.blockTag);
    }
    _userHit(address, overrides) {
        return (this._blockHit(overrides) &&
            (address === undefined || address === this.store.connection.userAddress));
    }
    hasStore(store) {
        return store === undefined || store === "blockPolled";
    }
    async getTotalRedistributed(overrides) {
        return this._blockHit(overrides)
            ? this.store.state.totalRedistributed
            : this._readable.getTotalRedistributed(overrides);
    }
    async getTroveBeforeRedistribution(address, overrides) {
        return this._userHit(address, overrides)
            ? this.store.state.troveBeforeRedistribution
            : this._readable.getTroveBeforeRedistribution(address, overrides);
    }
    async getTrove(address, overrides) {
        return this._userHit(address, overrides)
            ? this.store.state.trove
            : this._readable.getTrove(address, overrides);
    }
    async getNumberOfTroves(overrides) {
        return this._blockHit(overrides) ? this.store.state.numberOfTroves : this._readable.getNumberOfTroves(overrides);
    }
    async getCollPrice(address, overrides) {
        return this._blockHit(overrides) ? this.store.state.underlyingPrices[address] : this._readable.getCollPrice(address, overrides);
    }
    async getUnderlyingDecimals(vaultTokens, overrides) {
        return this._blockHit(overrides) ? this.store.state.underlyingDecimals : this._readable.getUnderlyingDecimals(vaultTokens);
    }
    async getUnderlyingTokens(vaultTokens, overrides) {
        return this._blockHit(overrides) ? this.store.state.underlyingTokens : this._readable.getUnderlyingTokens(vaultTokens);
    }
    async getCollPrices(addresses, vaultTokens, underlyingDecimals, overrides) {
        // const prices:TroveMappings = {};
        // for (let i = 0; i < addresses.length; i++) {
        //   // double check this
        //   prices[addresses[i]] = await this._readable.getCollPrice(addresses[i]);
        // }
        return this._blockHit(overrides) ? this.store.state.underlyingPrices : this._readable.getCollPrices(addresses, vaultTokens, underlyingDecimals, overrides);
    }
    async getPrices(addresses, overrides) {
        // const prices:TroveMappings = {};
        // for (let i = 0; i < addresses.length; i++) {
        //   // double check this
        //   prices[addresses[i]] = await this._readable.getCollPrice(addresses[i]);
        // }
        return this._blockHit(overrides) ? this.store.state.prices : this._readable.getPrices(addresses);
    }
    async getReceiptPerUnderlyingRatios(addresses, vaultTokens, overrides) {
        return this._blockHit(overrides) ? this.store.state.receiptPerUnderlyingRatios : this._readable.getReceiptPerUnderlyingRatios(addresses, vaultTokens);
    }
    async getUnderlyingPerReceiptRatios(addresses, vaultTokens, overrides) {
        return this._blockHit(overrides) ? this.store.state.underlyingPerReceiptRatios : this._readable.getUnderlyingPerReceiptRatios(addresses, vaultTokens);
    }
    async getBalances(addresses, overrides) {
        return this._blockHit(overrides) ? this.store.state.tokenBalances : this._readable.getBalances(addresses);
    }
    async getLPBalances(addresses, overrides) {
        return this._blockHit(overrides) ? this.store.state.lpTokenBalance : this._readable.getLPBalances(addresses);
    }
    async getTotal(overrides) {
        return this._blockHit(overrides) ? this.store.state.total : this._readable.getTotal(overrides);
    }
    async getStabilityDeposit(address, overrides) {
        return this._userHit(address, overrides)
            ? this.store.state.stabilityDeposit
            : this._readable.getStabilityDeposit(address, overrides);
    }
    async getFarm(address, overrides) {
        return this._userHit(address, overrides)
            ? this.store.state.farm
            : this._readable.getFarm(address, overrides);
    }
    async getBoostedFarm(address, overrides) {
        return this._userHit(address, overrides)
            ? this.store.state.boostedFarm
            : this._readable.getBoostedFarm(address, overrides);
    }
    async getVEYETIStake(address, overrides) {
        return this._userHit(address, overrides)
            ? this.store.state.veYETIStaked
            : this._readable.getVEYETIStake(address, overrides);
    }
    async getPoolRewardRate(overrides) {
        return this._blockHit(overrides)
            ? this.store.state.poolRewardRate
            : this._readable.getPoolRewardRate(overrides);
    }
    async getRemainingStabilityPoolYETIReward(overrides) {
        return this._blockHit(overrides)
            ? this.store.state.remainingStabilityPoolYETIReward
            : this._readable.getRemainingStabilityPoolYETIReward(overrides);
    }
    async getYUSDInStabilityPool(overrides) {
        return this._blockHit(overrides)
            ? this.store.state.yusdInStabilityPool
            : this._readable.getYUSDInStabilityPool(overrides);
    }
    getDepositFee(VCInput, VCOutput, overrides) {
        return this._readable.getDepositFee(VCInput, VCOutput, overrides);
    }
    getEstimatedFarmRewards(amount, time, overrides) {
        return this._readable.getEstimatedFarmRewards(amount, time, overrides);
    }
    async getEstimatedVeYetiRewards(amount, time, overrides) {
        return this._readable.getEstimatedVeYetiRewards(amount, time, overrides);
    }
    getEstimatedYETIPoolRewards(amount, time, overrides) {
        return this._readable.getEstimatedYETIPoolRewards(amount, time, overrides);
    }
    // async getRiskiestTrove(overrides?: EthersCallOverrides): Promise<TroveWithPendingRedistribution> {
    //   return this._blockHit(overrides)
    //     ? this.store.state._riskiestTroveBeforeRedistribution
    //     : this._readable.getRiskiestTrove(overrides);
    // }
    async getBalanceERC20(address, overrides) {
        const { userAddress } = this.connection;
        return this._userHit(userAddress, overrides)
            ? this.store.state.tokenBalances[address !== null && address !== void 0 ? address : '0']
            : this._readable.getBalanceERC20(address);
    }
    async getLP(address, overrides) {
        const { userAddress } = this.connection;
        return this._userHit(userAddress, overrides)
            ? this.store.state.tokenBalances[address !== null && address !== void 0 ? address : '0']
            : this._readable.getLP(address);
    }
    async hasClaimableCollateral(address, overrides) {
        return this._readable.hasClaimableCollateral(address);
    }
    async getRedemptionBonus(address, overrides) {
        return this._readable.getRedemptionBonus(address);
    }
    async getRedemptionFeeRate(overrides) {
        return this._readable.getRedemptionFeeRate(overrides);
    }
    async getReceiptPerUnderlying(address) {
        return this._readable.getReceiptPerUnderlying(address);
    }
    async getUnderlyingPerReceipt(address) {
        return this._readable.getUnderlyingPerReceipt(address);
    }
    async getUnderlyingDecimal(address) {
        return this._readable.getUnderlyingDecimal(address);
    }
    async getUnderlyingToken(address) {
        return this._readable.getUnderlyingToken(address);
    }
    async getSortedTroveHead() {
        return this._readable.getSortedTroveHead();
    }
    async getSortedTroveTail() {
        return this._readable.getSortedTroveTail();
    }
    async getSortedTroveSize() {
        return this._readable.getSortedTroveSize();
    }
    async getSortedTroveNext(id) {
        return this._readable.getSortedTroveNext(id);
    }
    async getSortedTrovePrev(id) {
        return this._readable.getSortedTrovePrev(id);
    }
    async getCurrentAICR(borrower) {
        return this._readable.getCurrentAICR(borrower);
    }
    async getTroveDebt(borrower) {
        return this._readable.getTroveDebt(borrower);
    }
    async getAllowanceOf(owner, token, spender, amount, overrides) {
        return this._readable.getAllowanceOf(owner, token, spender, amount);
    }
    async getYusdAllowance(from, to, amount) {
        return this._readable.getYusdAllowance(from, to, amount);
    }
    async getYETIPrice() {
        return this._readable.getYETIPrice();
    }
    async getYUSDPrice() {
        return this._readable.getYUSDPrice();
    }
    async getGlobalBoostFactor() {
        return this._readable.getGlobalBoostFactor();
    }
    async getDecayedBoost(address) {
        return this._readable.getDecayedBoost(address);
    }
    async getYUSDBalance(address, overrides) {
        return this._userHit(address, overrides)
            ? this.store.state.yusdBalance
            : this._readable.getYUSDBalance(address, overrides);
    }
    async getYETIBalance(address, overrides) {
        return this._userHit(address, overrides)
            ? this.store.state.yetiBalance
            : this._readable.getYETIBalance(address, overrides);
    }
    async getCollateralSurplusBalancee(overrides) {
        return this._blockHit(overrides)
            ? this.store.state.collateralSurplusBalance
            : this._readable.getCollateralSurplusBalancee(overrides);
    }
    async _getBlockTimestamp(blockTag) {
        return this._blockHit({ blockTag })
            ? this.store.state.blockTimestamp
            : this._readable._getBlockTimestamp(blockTag);
    }
    async _getFeesFactory(overrides) {
        return this._blockHit(overrides)
            ? this.store.state._feesFactory
            : this._readable._getFeesFactory(overrides);
    }
    async getFees(overrides) {
        return this._blockHit(overrides) ? this.store.state.fees : this._readable.getFees(overrides);
    }
    async getVaultTokens(whitelistedCollaterals, overrides) {
        return this._blockHit(overrides)
            ? this.store.state.vaultTokens
            : this._readable.getVaultTokens(whitelistedCollaterals, overrides);
    }
    async getWhitelistedCollaterals(overrides) {
        return this._blockHit(overrides)
            ? this.store.state.whitelistedCollaterals
            : this._readable.getWhitelistedCollaterals(overrides);
    }
    async getICR(address, overrides) {
        return this._userHit(address, overrides)
            ? this.store.state.icr
            : this._readable.getICR(address, overrides);
    }
    async getVcValue(address, overrides) {
        return this._userHit(address, overrides)
            ? this.store.state.vcValue
            : this._readable.getVcValue(address, overrides);
    }
    async getSafetyRatios(addresses, overrides) {
        return this._blockHit(overrides)
            ? this.store.state.safetyRatios
            : this._readable.getSafetyRatios(addresses, overrides);
    }
    async getRecoveryRatios(addresses, overrides) {
        return this._blockHit(overrides)
            ? this.store.state.recoveryRatios
            : this._readable.getRecoveryRatios(addresses, overrides);
    }
    async getDecimals(addresses, overrides) {
        return this._blockHit(overrides)
            ? this.store.state.decimals
            : this._readable.getDecimals(addresses, overrides);
    }
    getTroves(params, overrides) {
        return this._readable.getTroves(params, overrides);
    }
    _getActivePool() {
        throw new Error("Method not implemented.");
    }
    _getDefaultPool() {
        throw new Error("Method not implemented.");
    }
}
//# sourceMappingURL=ReadableEthersLiquity.js.map